import type { KeyChord } from "../services/commands/commands.types";

/** Screen-reader text for keyboard navigation of a grid of tiles. */
export const gridKeyboardCopy = {
  /** Fallback when a tile has no title. */
  tileFallbackLabel: "Tile",
  /** Announced when a tile takes focus. */
  focused: (label: string, row: number, column: number) => `${label}, row ${row}, column ${column}`,
  /** Announced after Alt+Arrow moved a tile. */
  moved: (label: string, row: number, column: number) => `Moved ${label} to row ${row}, column ${column}`,
  /** Announced after Alt+Shift+Arrow resized a tile. */
  resized: (label: string, columns: number, rows: number) => `Resized ${label} to ${columns} columns by ${rows} rows`,
  /** Announced when an arrangement key could not change anything. */
  blocked: (label: string) => `${label} can't go further that way`,
  /** Announced when Space shows a tile's buttons. */
  chromeShown: (label: string) => `${label} actions shown. Press Tab to reach them, Escape to go back`,
} as const;

const ARROWS = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"] as const;

/** The shortcut sheet rows for the grid, in display order (every chord of a row is shown). */
export const gridKeyboardShortcuts: ReadonlyArray<{ id: string; action: string; chords: readonly KeyChord[] }> = [
  { id: "grid.enter", action: "Enter the grid on the last-focused tile", chords: [{ key: "Tab" }] },
  { id: "grid.focus", action: "Move focus to the nearest tile", chords: ARROWS.map((key) => ({ key })) },
  { id: "grid.ends", action: "First / last tile", chords: [{ key: "Home" }, { key: "End" }] },
  { id: "grid.edit", action: "Edit the tile", chords: [{ key: "Enter" }] },
  { id: "grid.menu", action: "Open the tile's more menu", chords: [{ key: "F10", shift: true }, { key: "ContextMenu" }] },
  { id: "grid.delete", action: "Delete the tile (asks first)", chords: [{ key: "Delete" }, { key: "Backspace" }] },
  { id: "grid.duplicate", action: "Duplicate the tile", chords: [{ key: "d", mod: true }] },
  { id: "grid.chrome", action: "Show the tile's buttons", chords: [{ key: " " }] },
  { id: "grid.back", action: "Back to the grid", chords: [{ key: "Escape" }] },
  { id: "grid.move", action: "Move the tile one cell (Build mode)", chords: ARROWS.map((key) => ({ key, alt: true })) },
  { id: "grid.resize", action: "Resize the tile one cell (Build mode)", chords: ARROWS.map((key) => ({ key, alt: true, shift: true })) },
];
